import { defineStore } from 'pinia';
import { $settings } from '@/provider/settings';
import axios from '@/axios';
import { useOverviewStore } from '@/store/overview';
import { useObjectStore } from '@/store/object';
import { $i18n } from '@/provider/plugin';

const sapUrl = $settings.middlewareSAPProxyURI;

const DASHBOARD_SYSTEM = '00000000000000000000000000000000';

// TODO: BE should remove "GUID:" from the response
const extractGUID = (guidString) => {
  if (guidString.includes('GUID:')) return guidString.split(':')[1];
  return guidString || null;
};

export const useDashboardStore = defineStore('dashboard', {
  state: () => ({
    dashboardConfig: {},
    defaultDashboard: '',
    selectedDashboard: null,
    dashboards: [],
    dashboardItems: new Map(),
    dashboardLoading: {},
  }),
  getters: {
    getDashboardConfig: (state) => state.dashboardConfig || {},
    getDashboardList: (state) => state.dashboardList || [],
    getDefaultDashboard: (state) => state.defaultDashboard,
    getSelectedDashboard: (state) => state.selectedDashboard,
    getDashboardLoading: (state) => state.dashboardLoading || {},
    getDashboards: (state) => state.dashboards || [],
    getDashboardItems: (state) => (type) =>
      state.dashboardItems.get(type) || [],
  },
  actions: {
    setDashboardLoading(area, isLoading) {
      this.dashboardLoading[area] = isLoading;
    },
    resetSelectedDashboard() {
      this.selectedDashboard = null;
    },
    initDashboard(data) {
      this.dashboardConfig = data.content;
    },
    async fetchDashboardData({
      type,
      subType,
      subTypes,
      options = {},
      totals = false,
    }) {
      if (!subType && !subTypes) {
        console.warn('No subType or subTypes for dashboard call provided');
      }

      const records =
        subTypes && subTypes.length > 0 ? subTypes.join(',') : subType;

      const { skip = 0, activeSort = {}, pageAll } = options;

      const getTotals = totals ? '&totals=X' : '';
      const skipParameter = `&skip=${skip || 0}`;
      const pageSizeParameter = pageAll ? '&pageSize=999999' : '';
      const activeSortParameter =
        (activeSort.sort &&
          `&sort=${activeSort.sort} ${activeSort.asc ? 'asc' : 'desc'}`) ||
        '';

      try {
        const response = await axios.get(
          `${sapUrl}/${type}/dashboard/?Records=${records}${skipParameter}${activeSortParameter}${pageSizeParameter}${getTotals}`,
        );

        if (response?.data?.DATA) {
          const { DATA, INFO } = response.data;
          const mappedData = {};

          (subTypes || [subType]).forEach((indexSubType) => {
            mappedData[indexSubType] = indexSubType.includes('GUID')
              ? {
                  value: DATA || [],
                  total: INFO.TOTAL || 0,
                }
              : {
                  value: DATA[indexSubType] || [],
                  total: (DATA.TOTALS && DATA.TOTALS[indexSubType]) || 0,
                };

            useOverviewStore().setOverview({
              context: pageAll ? 'queryAll' : 'query',
              type,
              subType: indexSubType,
              payload: mappedData[indexSubType],
            });
          });
        }
      } catch (error) {
        console.error(`Couldn't fetch dashboard data:`, error);
      }
    },
    async fetchDashboardConfig({ type, subType, subTypes }) {
      const records =
        subTypes && subTypes.length > 0 ? `?Records=${subTypes.join(',')}` : '';

      try {
        const response = await axios.get(
          `${sapUrl}/${type}/dashboard/${records}`,
        );

        if (response && response.data && response.data.DATA) {
          const mappedData = {};

          (subTypes || [subType]).forEach((indexSubType) => {
            mappedData[indexSubType] = response.data.DATA[indexSubType] || [];

            useOverviewStore().setOverview({
              context: 'query',
              type,
              subType: indexSubType,
              payload: mappedData[indexSubType],
            });
          });
        }
      } catch (error) {
        console.error(`Couldn't fetch dashboard config:`, error);
      }
    },
    async updateDashboard({ payload, type }) {
      try {
        const response = await axios.post(`${sapUrl}/${type}/dashboard/`, {
          QUERYRESULT: payload,
        });

        const { UPD_AREAS = [], SUCCESS } = response.data;

        if (!SUCCESS) return {};

        // TODO: BE should provide correct format
        const updateAreasMapped = UPD_AREAS.map(
          ({ PARENT, BOTYPE, SUBTYPE }) => ({
            type: BOTYPE,
            subType: SUBTYPE,
            parent: PARENT,
          }),
        );
        return updateAreasMapped;
      } catch (error) {
        console.error('Failed to update dashboard:', error);
        return {};
      }
    },
    async fetchDashboards() {
      this.setDashboardLoading('dashboardOverview', true);

      try {
        const response = await axios.get(
          `${$settings.middlewareSAPProxyURI}/config/dashboard`,
        );

        if (response?.data?.DATA) {
          const fallbackGuid = DASHBOARD_SYSTEM;
          const defaultDashboard = response.data.DATA.find(
            (dashboard) => dashboard.default,
          );

          const payload = response.data.DATA.map((item, index) => ({
            ...item,
            itemId: index,
            visibilityDescr: item.teamDescr,
            default: !!item.default,
            favorite: !!item.favorite,
          }));

          this.defaultDashboard = defaultDashboard?.guid || fallbackGuid;

          useOverviewStore().setOverview({
            context: 'dashboardList',
            type: 'dashboard',
            payload,
          });
        }
      } catch (error) {
        console.error(`Couldn't fetch dashboards:`, error);
      } finally {
        this.setDashboardLoading('dashboardOverview', false);
      }
    },
    async addDashboard(payload) {
      try {
        const response = await axios.post(
          `${sapUrl}/config/dashboard`,
          payload,
        );

        if (response?.data?.DATA) {
          return extractGUID(response.data.DATA);
        }

        return null;
      } catch (error) {
        console.error(`Couldn't add dashboard:`, error);
        return null;
      } finally {
        await this.fetchDashboards();
      }
    },
    async saveSelectedDashboard({ dashboard, guid }) {
      try {
        await axios.put(`${sapUrl}/config/dashboard/${guid}`, dashboard);

        this.selectedDashboard = dashboard;

        await this.fetchDashboards();
      } catch (error) {
        console.error('Could not save selected dashboard:', error);
      }
    },
    async deleteDashboard(guid) {
      try {
        await axios.delete(
          `${$settings.middlewareSAPProxyURI}/config/dashboard/${guid}`,
        );

        if (this.selectedDashboard?.guid === guid) {
          this.selectedDashboard = null;
        }

        await this.fetchDashboards();
      } catch (error) {
        console.error(`Couldn't delete dashboard [${guid}]:`, error);
      }
    },
    async loadDashboard(guid) {
      if (guid === DASHBOARD_SYSTEM) {
        // TODO: Need Alias for System
        return {
          guid: DASHBOARD_SYSTEM,
          name: 'System',
          system: true,
        };
      }

      this.setDashboardLoading('dashboardDetail', true);

      try {
        const response = await axios.get(`${sapUrl}/config/dashboard/${guid}`);

        return response?.data?.DATA || null;
      } catch (error) {
        console.error(`Couldn't load dashboard ${guid}:`, error);
        return null;
      } finally {
        this.setDashboardLoading('dashboardDetail', false);
      }
    },
    async setSelectedDashboard(dashboard) {
      const { guid, owner } = dashboard;
      const dashboardData = await this.loadDashboard(guid);

      if (dashboardData) {
        this.selectedDashboard = {
          ...dashboardData,
          guid,
          owner,
        };
      }
    },
    async getWebsiteItems() {
      try {
        const response = await axios.post(`${sapUrl}/dashboard/autosuggest`, {
          SOURCE: {
            RECORD: 'dashboard',
            FIELD: 'weblink',
          },
        });

        if (response?.data) {
          this.dashboardItems.set(
            'website',
            response.data.map(({ LINK, TEXT_ALIAS, ICON, COLOR }) => {
              const type = `website-${ICON}`;

              useObjectStore(type).settings = {
                color: COLOR,
                icon: ICON,
              };

              return {
                text: $i18n.global.t(TEXT_ALIAS),
                value: LINK,
                icon: ICON,
                color: COLOR,
                type,
              };
            }),
          );
        }
      } catch (error) {
        console.error('Failed to get website items:', error);
      }
    },
    async getSmartKpisItems() {
      try {
        const response = await axios.post(`${sapUrl}/dashboard/autosuggest`, {
          SOURCE: {
            RECORD: 'dashboard',
            FIELD: 'block',
          },
        });

        if (response?.data) {
          this.dashboardItems.set(
            'smart_kpis',
            response.data.map(({ ID, TITLE, TYPE }) => ({
              text: TITLE,
              value: ID,
              type: TYPE,
            })),
          );
        }
      } catch (error) {
        console.error(`Couldn't load Smart KPIs:`, error);
      }
    },
    async getDashboardBOItems(type) {
      try {
        const response = await axios.get(
          `${sapUrl}/config/tvalue/dashboard/${type}`,
        );

        if (response?.data?.DATA) {
          this.dashboardItems.set(type, response.data.DATA);
        }
      } catch (error) {
        console.error(
          `Failed to fetch dashboard BO items for type: ${type}`,
          error,
        );
      }
    },
    async getChartsItems() {
      try {
        const response = await axios.post(
          `${$settings.middlewareSAPProxyURI}/dashboard/autosuggest`,
          {
            SOURCE: {
              RECORD: 'dashboard',
              FIELD: 'chart',
            },
          },
        );

        if (response?.data) {
          this.dashboardItems.set(
            'charts',
            response.data.map(({ ID, TITLE, TYPE, CHART_TYPE }) => ({
              text: TITLE,
              value: ID,
              type: TYPE,
              chartType: CHART_TYPE,
            })),
          );
        }
      } catch (error) {
        console.error(`Couldn't fetch charts items:`, error);
      }
    },
  },
});
